//SpecTable.vue
<script setup>
import { computed, ref, watch } from 'vue'
import AdapButtom from '@/components/AdapButtom.vue'
import TableLine from '@/components/TableLine.vue'

const props = defineProps({
  specData: {
    type: Array,
    required: true
  }
})

const toGroupedTab = (sections) => ({
  key: sections.map((section) => section.key).join(' & '),
  sections
})

const isAntennaGnssPair = (firstLabel = '', secondLabel = '') => {
  const first = String(firstLabel).toLowerCase()
  const second = String(secondLabel).toLowerCase()

  return (
    (first.includes('天线') || first.includes('antenna')) &&
    second.includes('gnss')
  )
}

const buildTabGroups = (items) => {
  if (items.length <= 4) {
    return items.map((item) => [item])
  }

  if (items.length === 10) {
    return [
      items.slice(0, 2),
      items.slice(2, 4),
      items.slice(4, 6),
      items.slice(6, 8),
      items.slice(8, 10)
    ]
  }

  if (items.length === 9) {
    return [
      items.slice(0, 1),
      items.slice(1, 3),
      items.slice(3, 5),
      items.slice(5, 7),
      items.slice(7, 9)
    ]
  }

  if (items.length === 7) {
    return [
      items.slice(0, 1),
      items.slice(1, 3),
      items.slice(3, 5),
      items.slice(5, 7)
    ]
  }

  if (items.length === 5) {
    if (isAntennaGnssPair(items[1]?.key, items[2]?.key)) {
      return [
        items.slice(0, 1),
        items.slice(1, 3),
        items.slice(3, 5)
      ]
    }

    return [
      items.slice(0, 2),
      items.slice(2, 4),
      items.slice(4, 5)
    ]
  }

  const groups = []
  let start = 0

  while (start < items.length) {
    groups.push(items.slice(start, start + 2))
    start += 2
  }

  return groups
}

const displaySpecData = computed(() => buildTabGroups(props.specData).map(toGroupedTab))
const currentKey = ref(displaySpecData.value[0]?.key || '')

watch(displaySpecData, (items) => {
  if (!items.some((item) => item.key === currentKey.value)) {
    currentKey.value = items[0]?.key || ''
  }
})
</script>

<template>
  <div class="spec-table">
    <div class="spec-table__buttons">
      <AdapButtom
        v-for="item in displaySpecData"
        :key="item.key"
        class="spec-table__button"
        :class="{ 'spec-table__button--active': currentKey === item.key }"
        :text="item.key"
        @mouseover="currentKey = item.key"
        @click="currentKey = item.key"
        @focus="currentKey = item.key"
      />
    </div>

    <div class="spec-table__content">
      <template v-for="item in displaySpecData" :key="item.key">
        <div v-show="currentKey === item.key">
          <section
            v-for="section in item.sections"
            :key="section.key"
            class="spec-table__section"
          >
            <h3 v-if="item.sections.length > 1" class="spec-table__section-title">
              {{ section.key }}
            </h3>
            <TableLine
              v-for="(row, index) in section.tableData"
              :key="index"
              :title="row.title"
              :contentList="row.contentList"
            />
          </section>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.spec-table__buttons {
  justify-content: center;
  display: flex;
  gap: 40px;
  margin-bottom: 24px;
  padding-bottom: 60px;
  border-bottom: 0.5px solid rgb(217, 217, 217);
}

.spec-table__section-title {
  margin: 0 0 20px;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-24);
  line-height: var(--pixso-lh-24);
  font-weight: 700;
}

.spec-table__section + .spec-table__section {
  margin-top: 48px;
}
</style>
